import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import {
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  FontAwesomeIcon,
  Select,
  Stack,
  Title,
} from "@skailer-com/uikit";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useJobDetails } from "@/queries/recruit/use-jobs.query.ts";
import {
  useScoreCardAddParticipants,
  useScoreCardSearchInline,
} from "@/queries/recruit/use-scorecards.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployee, EmployeeFilterDefault } from "@/types/employees/employee.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface ScoreCardsRequestModal extends ModalProps {
  candidateId: number;
  applicantId: number;
  employeeId: number;
  jobId: number;
}

export function ScoreCardsRequestModal({ candidateId, jobId, ...props }: ScoreCardsRequestModal) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<ApiEmployee[]>([]);
  const { id } = props;
  const { close } = useModals();
  const { showInfo } = useToasts();

  const ScoreCardsRequestSchemaObject = z.object({
    scorecard_id: z.number({ error: t("errors:field_required") }),
    employee_ids: z.array(z.number()).min(1, t("scorecards:error_select_at_least_one_participant")),
  });

  type ScoreCardsRequestSchema = z.infer<typeof ScoreCardsRequestSchemaObject>;

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<ScoreCardsRequestSchema>({
    mode: "onSubmit",
    resolver: zodResolver(ScoreCardsRequestSchemaObject),
  });

  const addParticipants = useScoreCardAddParticipants(control);
  const scoreCardsSearch = useScoreCardSearchInline();
  const job = useJobDetails(jobId);
  const employeesSearch = useEmployeeSearchInline(true, {
    ...EmployeeFilterDefault,
    exclude_ids: employees.map((employee) => employee.id),
  });

  const employeeIds = useMemo(() => {
    return employees.map((employee) => employee.id);
  }, [employees]);

  const recruiters = useMemo(() => {
    return (job?.data?.recruiters ?? []).filter((employee) => !employeeIds.includes(employee.id));
  }, [job, employeeIds]);

  const stakeholders = useMemo(() => {
    return (job?.data?.stakeholders ?? []).filter((employee) => !employeeIds.includes(employee.id));
  }, [job, employeeIds]);

  useEffect(() => {
    const employeeIds: number[] = employees.map((employee) => employee.id);

    employeesSearch.setFilter({
      ...employeesSearch.filter,
      exclude_ids: employeeIds,
    });

    setValue("employee_ids", employeeIds);
  }, [employeeIds]);

  const removeParticipant = (id: number) => {
    setEmployees(employees.filter((employee) => employee.id !== id));
  };

  const onSubmit = handleSubmit(async (data) => {
    const result = await addParticipants.mutateAsync({
      scoreCardId: data.scorecard_id,
      candidateId: candidateId,
      employeeIds: data.employee_ids,
    });

    if (!result.success) return;

    analyticsService.trackEvent(analyticEvents.scoreCards.invitesSent, {
      [analyticProperties.id]: candidateId,
    });

    showInfo({ title: t("scorecards:invitations_sent") });
    close(id);
  });

  return (
    <Modal
      {...props}
      title={t("scorecards:request_feedback")}
      withCloser={true}
      actions={
        <Stack>
          <Button size={"lg"} onClick={onSubmit} isLoading={addParticipants.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("scorecards:scorecards")}
                emptyTitle={t("common:select")}
                isLoading={scoreCardsSearch.isLoading}
                value={value}
                searchable
                isClearable
                options={scoreCardsSearch.options}
                onSearchOptions={scoreCardsSearch.search}
                loadOptionsOnOpen
                searchOptions={scoreCardsSearch.options}
                onChange={(value) => {
                  onChange(value);
                }}
                error={errors.scorecard_id?.message}
              />
            )}
            name={"scorecard_id"}
            control={control}
          />
          <Title size={"xs"} header={t("common:participants")} paddingTop paddingBottom />
          <Select
            label={t("calendar:add_guests")}
            emptyTitle={t("common:select")}
            value={null}
            options={[]}
            searchable
            showAvatar={true}
            showCaption={true}
            onSearchOptions={(text) => employeesSearch.search(text)}
            searchOptions={employeesSearch.options}
            loadOptionsOnOpen
            onChange={(e) => {
              const employee = employeesSearch.data?.items.find((x) => x.id == e);

              if (employee) {
                setEmployees([...employees, employee]);
              }
            }}
            error={errors.employee_ids?.message}
          />
          <Stack>
            {employees.map((employee) => (
              <EmployeeListItem
                key={`employee-${employee.id}`}
                employee={employee}
                valueSlot={
                  <Button
                    variant={"secondary"}
                    onClick={() => {
                      removeParticipant(employee.id);
                    }}
                    leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  />
                }
              />
            ))}
          </Stack>
          {recruiters.length > 0 && (
            <Stack>
              <Title size={"xs"} header={t("ats:recruiters")} paddingTop paddingBottom />
              {recruiters.map((employee) => (
                <EmployeeListItem
                  key={`employee-${employee.id}`}
                  employee={employee}
                  valueSlot={
                    <Button
                      variant={"secondary"}
                      onClick={() => {
                        setEmployees([...employees, employee]);
                      }}
                    >
                      {t("common:add")}
                    </Button>
                  }
                />
              ))}
            </Stack>
          )}
          {stakeholders.length > 0 && (
            <Stack>
              <Title size={"xs"} header={t("ats:stakeholders")} paddingTop paddingBottom />
              {stakeholders.map((employee) => (
                <EmployeeListItem
                  key={`employee-${employee.id}`}
                  employee={employee}
                  valueSlot={
                    <Button
                      variant={"secondary"}
                      onClick={() => {
                        setEmployees([...employees, employee]);
                      }}
                    >
                      {t("common:add")}
                    </Button>
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
